import { useEffect, useState } from 'react';
import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/src/shadComponents/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from '@/src/shadComponents/ui/dialog';
import useWhitelabelSettings from './hooks/useWhitelabelSettings';
import { WhitelabelSettingsInfo } from '../lib/types';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import PageBreadcrumb from '@/src/Common/components/PageBreadcrumb';

export default function WhitelabelSettingsDetail() {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState<WhitelabelSettingsInfo | null>(
        null,
    );
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const navigate = useNavigate();
    const { getWhitelabelSettings, deleteWhitelabelSettings } =
        useWhitelabelSettings();
    const { org } = useUserOrg();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setLoading(true);
                if (!id) {
                    toast.error('Settings ID is required');
                    navigate('/org/whitelabel-settings');
                    return;
                }

                const settingsData = await getWhitelabelSettings(
                    parseInt(id, 10),
                    org?.id,
                );
                setSettings(settingsData);
            } catch (error) {
                console.error('Error fetching whitelabel settings:', error);
                toast.error('Failed to load whitelabel settings');
                navigate('/org/whitelabel-settings');
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = async () => {
        try {
            if (!id) return;

            setLoading(true);
            await deleteWhitelabelSettings(parseInt(id, 10), org?.id);
            toast.success('Whitelabel settings deleted successfully');
            navigate('/org/whitelabel-settings');
        } catch (error) {
            console.error('Error deleting whitelabel settings:', error);
            toast.error('Failed to delete whitelabel settings');
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!settings) {
        return <div className="p-4">Whitelabel settings not found</div>;
    }

    return (
        <div className="container mx-auto p-4">
            <div className="mb-4">
                <PageBreadcrumb crumbs={[{ label: 'Whitelabel', href: '/org/whitelabel-settings' }, { label: settings?.name || 'Setting' }]} />
            </div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">{settings.name}</h1>
                <div className="flex space-x-3">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/org/whitelabel-settings')}
                    >
                        Back to List
                    </Button>
                    <Button
                        variant="default"
                        onClick={() =>
                            navigate(`/org/whitelabel-settings/${id}/edit`)
                        }
                    >
                        <Pencil className="size-4 mr-2" />
                        Edit
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => setShowDeleteDialog(true)}
                    >
                        <Trash2 className="size-4 mr-2" />
                        Delete
                    </Button>
                </div>
            </div>

            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Whitelabel Settings Details</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Name
                            </h3>
                            <p className="mt-1">{settings.name}</p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Domain
                            </h3>
                            <p className="mt-1">
                                <a
                                    href={settings.domain}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                >
                                    {settings.domain}
                                </a>
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Email From Name
                            </h3>
                            <p className="mt-1">{settings.email_from_name}</p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Email From Address
                            </h3>
                            <p className="mt-1">
                                {settings.email_from_address}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Email Reply-To Address
                            </h3>
                            <p className="mt-1">
                                {settings.email_reply_to_address}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Last Updated
                            </h3>
                            <p className="mt-1">
                                {new Date(settings.updated_at).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Email Preview</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="border rounded-md p-4">
                        <div className="border-b pb-3">
                            <p>
                                <strong>From:</strong>{' '}
                                {settings.email_from_name} &lt;
                                {settings.email_from_address}&gt;
                            </p>
                            <p>
                                <strong>Reply-To:</strong>{' '}
                                {settings.email_reply_to_address}
                            </p>
                            <p>
                                <strong>Subject:</strong> Your Photos Are Ready
                            </p>
                        </div>
                        <div className="mt-3">
                            <p>
                                This is a preview of how emails will appear when
                                sent using these whitelabel settings.
                            </p>
                            <p className="mt-2">
                                Recipients will see the sender name as "
                                {settings.email_from_name}" and if they reply,
                                their response will go to{' '}
                                {settings.email_reply_to_address}.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Deletion</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete "{settings.name}"?
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex space-x-2 justify-end">
                        <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="size-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
